import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faEdit,
  faBars,
  faChevronDown,
  faFileAlt,
  faSearch,
} from '@fortawesome/free-solid-svg-icons';
import { supabase } from '../lib/supabaseClient';
import DocumentRequirementModal from '../components/DocumentRequirementModal';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

function DocumentRequirements() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [documentRequirements, setDocumentRequirements] = useState([]);
  const [filteredRequirements, setFilteredRequirements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRequirement, setSelectedRequirement] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [userTypeFilter, setUserTypeFilter] = useState('all');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  const fetchDocumentRequirements = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('document_requirements')
        .select('*')
        .order('user_type', { ascending: true })
        .order('sort_order', { ascending: true });

      if (error) throw error;
      setDocumentRequirements(data || []);
      setFilteredRequirements(data || []);
    } catch (error) {
      console.error('Error fetching document requirements:', error);
      setError('Belge gereksinimleri yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocumentRequirements();
  }, []);

  useEffect(() => {
    let filtered = [...documentRequirements];

    // Apply user type filter
    if (userTypeFilter !== 'all') {
      filtered = filtered.filter(
        (req) => req.user_type === userTypeFilter || req.user_type === 'all'
      );
    }

    // Apply search filter
    if (searchQuery) {
      const lowercasedQuery = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (req) =>
          req.document_type.toLowerCase().includes(lowercasedQuery) ||
          req.name_en.toLowerCase().includes(lowercasedQuery) ||
          req.name_tr.toLowerCase().includes(lowercasedQuery) ||
          req.name_ru.toLowerCase().includes(lowercasedQuery)
      );
    }

    setFilteredRequirements(filtered);
    setCurrentPage(1);
  }, [searchQuery, documentRequirements, userTypeFilter]);

  const handleAddNew = () => {
    setSelectedRequirement(null);
    setIsModalOpen(true);
  };

  const handleEdit = (requirement) => {
    setSelectedRequirement(requirement);
    setIsModalOpen(true);
  };

  const handleToggleRequired = async (requirement) => {
    try {
      // Update local state immediately for better UI responsiveness
      const updatedRequirements = documentRequirements.map((req) =>
        req.id === requirement.id
          ? { ...req, is_required: !req.is_required }
          : req
      );
      setDocumentRequirements(updatedRequirements);

      // Then update in the database
      const { error } = await supabase
        .from('document_requirements')
        .update({ is_required: !requirement.is_required })
        .eq('id', requirement.id);

      if (error) {
        throw error;
      }

      // No need to fetch all requirements again, we've already updated the local state
      // Only fetch if there was an error to revert the changes
    } catch (error) {
      console.error('Error updating document requirement:', error);
      setError('Belge gereksinimi güncellenirken bir hata oluştu');
      // Revert the local state change if the API call failed
      fetchDocumentRequirements();
    }
  };

  const handleToggleActive = async (requirement) => {
    try {
      // Update local state immediately for better UI responsiveness
      const updatedRequirements = documentRequirements.map((req) =>
        req.id === requirement.id ? { ...req, active: !req.active } : req
      );
      setDocumentRequirements(updatedRequirements);

      // Then update in the database
      const { error } = await supabase
        .from('document_requirements')
        .update({ active: !requirement.active })
        .eq('id', requirement.id);

      if (error) {
        throw error;
      }

      // No need to fetch all requirements again, we've already updated the local state
      // Only fetch if there was an error to revert the changes
    } catch (error) {
      console.error('Error updating document requirement:', error);
      setError('Belge gereksinimi güncellenirken bir hata oluştu');
      // Revert the local state change if the API call failed
      fetchDocumentRequirements();
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleUserTypeFilterChange = (e) => {
    setUserTypeFilter(e.target.value);
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredRequirements.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const pageNumbers = [];
  for (
    let i = 1;
    i <= Math.ceil(filteredRequirements.length / itemsPerPage);
    i++
  ) {
    pageNumbers.push(i);
  }

  const handleEntriesPerPageChange = (number) => {
    setItemsPerPage(number);
    setCurrentPage(1);
    setIsDropdownOpen(false);
  };

  const getUserTypeLabel = (userType) => {
    switch (userType) {
      case 'driver':
        return 'Sürücü';
      case 'company':
        return 'Şirket';
      case 'all':
        return 'Tümü';
      default:
        return userType;
    }
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Top Navigation */}
      <header className="bg-white shadow">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button className="text-gray-500 lg:hidden">
                <FontAwesomeIcon icon={faBars} className="text-xl" />
              </button>
            </div>

            <div className="relative">
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 text-gray-700 hover:text-gray-900 focus:outline-none"
              >
                <span>{user?.email}</span>
                <FontAwesomeIcon icon={faChevronDown} className="text-xs" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-100">
        <div className="container mx-auto px-6 py-8">
          <div className="flex items-center justify-between pb-6">
            <div>
              <h1 className="text-2xl font-medium text-gray-800">
                Belge Gereksinimleri
              </h1>
              <p className="text-sm text-gray-500">
                Toplam Belge: {documentRequirements.length}
              </p>
            </div>
            <button
              onClick={() => handleAddNew()}
              className="bg-[#4263EB] text-white px-4 py-2 rounded-md hover:bg-[#3b5bd9] flex items-center"
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              Yeni Belge Ekle
            </button>
          </div>

          {/* Filters */}
          <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between">
            <div className="flex flex-col md:flex-row md:items-center mb-4 md:mb-0">
              <div className="relative mb-4 md:mb-0 md:mr-4">
                <input
                  type="text"
                  placeholder="Belge ara..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="w-full md:w-64 pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <FontAwesomeIcon
                  icon={faSearch}
                  className="absolute left-3 top-3 text-gray-400"
                />
              </div>
              <div className="relative">
                <select
                  value={userTypeFilter}
                  onChange={handleUserTypeFilterChange}
                  className="w-full md:w-48 px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">Tüm Kullanıcılar</option>
                  <option value="driver">Sürücüler</option>
                  <option value="company">Şirketler</option>
                </select>
              </div>
            </div>
          </div>

          {/* Document Requirements Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {loading ? (
              <div className="p-4 text-center">
                Belge gereksinimleri yükleniyor...
              </div>
            ) : error ? (
              <div className="p-4 text-center text-red-500">{error}</div>
            ) : (
              <>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Belge Tipi
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Belge Adı (TR)
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Kullanıcı Tipi
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Zorunlu
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Aktif
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Sıralama
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        İşlemler
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {currentItems.map((requirement) => (
                      <tr key={requirement.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <FontAwesomeIcon
                              icon={faFileAlt}
                              className="text-gray-400 mr-2"
                            />
                            <div className="text-sm font-medium text-gray-900">
                              {requirement.document_type}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {requirement.name_tr}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {getUserTypeLabel(requirement.user_type)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div
                              onClick={() => handleToggleRequired(requirement)}
                              className="relative inline-block w-9 h-5 cursor-pointer"
                              title={
                                requirement.is_required
                                  ? 'Zorunlu (Opsiyonel yap)'
                                  : 'Opsiyonel (Zorunlu yap)'
                              }
                            >
                              <input
                                type="checkbox"
                                className="sr-only peer"
                                checked={requirement.is_required}
                                readOnly
                              />
                              <span
                                className={`absolute inset-0 rounded-full transition duration-200 ${
                                  requirement.is_required
                                    ? 'bg-green-500'
                                    : 'bg-gray-300'
                                }`}
                              ></span>
                              <span
                                className={`absolute inset-y-0 left-0 w-4 h-4 mt-0.5 ml-0.5 bg-white rounded-full transition-transform duration-200 transform ${
                                  requirement.is_required
                                    ? 'translate-x-4'
                                    : 'translate-x-0'
                                } shadow`}
                              ></span>
                            </div>
                            <span
                              className={`ml-3 text-sm ${
                                requirement.is_required
                                  ? 'text-green-600'
                                  : 'text-gray-500'
                              }`}
                            >
                              {requirement.is_required
                                ? 'Zorunlu'
                                : 'Opsiyonel'}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div
                              onClick={() => handleToggleActive(requirement)}
                              className="relative inline-block w-9 h-5 cursor-pointer"
                              title={
                                requirement.active
                                  ? 'Aktif (Pasif yap)'
                                  : 'Pasif (Aktif yap)'
                              }
                            >
                              <input
                                type="checkbox"
                                className="sr-only peer"
                                checked={requirement.active}
                                readOnly
                              />
                              <span
                                className={`absolute inset-0 rounded-full transition duration-200 ${
                                  requirement.active
                                    ? 'bg-green-500'
                                    : 'bg-gray-300'
                                }`}
                              ></span>
                              <span
                                className={`absolute inset-y-0 left-0 w-4 h-4 mt-0.5 ml-0.5 bg-white rounded-full transition-transform duration-200 transform ${
                                  requirement.active
                                    ? 'translate-x-4'
                                    : 'translate-x-0'
                                } shadow`}
                              ></span>
                            </div>
                            <span
                              className={`ml-3 text-sm ${
                                requirement.active
                                  ? 'text-green-600'
                                  : 'text-gray-500'
                              }`}
                            >
                              {requirement.active ? 'Aktif' : 'Pasif'}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {requirement.sort_order}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => handleEdit(requirement)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <FontAwesomeIcon icon={faEdit} className="mr-1" />
                            Düzenle
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination */}
                <div className="px-6 py-4 bg-white border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <p className="text-sm text-gray-700">
                        <span className="font-medium">
                          {indexOfFirstItem + 1}
                        </span>{' '}
                        -{' '}
                        <span className="font-medium">
                          {Math.min(
                            indexOfLastItem,
                            filteredRequirements.length
                          )}
                        </span>{' '}
                        /{' '}
                        <span className="font-medium">
                          {filteredRequirements.length}
                        </span>{' '}
                        arası gösteriliyor
                      </p>
                      <div className="relative ml-4">
                        <button
                          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                          className="border border-gray-300 rounded-md text-sm px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center"
                        >
                          {itemsPerPage}{' '}
                          <FontAwesomeIcon
                            icon={faChevronDown}
                            className="ml-1"
                          />
                        </button>
                        {isDropdownOpen && (
                          <div className="absolute z-10 mt-1 w-24 bg-white shadow-lg rounded-md py-1">
                            {[5, 10, 25, 50, 100].map((number) => (
                              <button
                                key={number}
                                onClick={() =>
                                  handleEntriesPerPageChange(number)
                                }
                                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              >
                                {number}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <button
                          onClick={() => paginate(currentPage - 1)}
                          disabled={currentPage === 1}
                          className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                            currentPage === 1
                              ? 'text-gray-300 cursor-not-allowed'
                              : 'text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          <span className="sr-only">Previous</span>
                          <FontAwesomeIcon
                            icon={faChevronDown}
                            className="transform rotate-90"
                          />
                        </button>
                        {pageNumbers.map((number) => (
                          <button
                            key={number}
                            onClick={() => paginate(number)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              currentPage === number
                                ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {number}
                          </button>
                        ))}
                        <button
                          onClick={() => paginate(currentPage + 1)}
                          disabled={
                            currentPage === pageNumbers.length ||
                            pageNumbers.length === 0
                          }
                          className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                            currentPage === pageNumbers.length ||
                            pageNumbers.length === 0
                              ? 'text-gray-300 cursor-not-allowed'
                              : 'text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          <span className="sr-only">Next</span>
                          <FontAwesomeIcon
                            icon={faChevronDown}
                            className="transform -rotate-90"
                          />
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </main>

      {/* Document Requirement Modal */}
      <DocumentRequirementModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        requirement={selectedRequirement}
        onSuccess={fetchDocumentRequirements}
      />
    </div>
  );
}

export default DocumentRequirements;
